import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { passwordChangeSchema } from '../../api/firebase/auth/validation';
import { auth } from '../../api/firebase/config';
import {
  blackText,
  primaryButtonBackground,
  primarycolor
} from '../../constants/Colors';
import {
  cardContentPadding,
  cardSize,
  textMb,
  titleMb,
  w100
} from '../../constants/Location';
import { throwError } from '../../modules/error';

import CustomLoading from './CustomLoading';
import CustomSnackbar from './CustomSnackbar';
import PasswordTextFiled from './PasswordTextField';

interface FormInputs {
  password: string;
  password_confirm: string;
}

const styles = {
  CheckboxM: {
    '@media screen and (max-width:480px)': {
      marginLeft: 0
    },
    marginLeft: '36px'
  },
  cardContentPadding,
  cardSize,
  required: {
    color: primarycolor,
    marginLeft: '10px'
  },
  textMb,
  textSmall: {
    fontSize: '11.5px'
  },
  titleMb,
  w100
};

const PasswordChangeCard: React.FC = () => {
  const { t } = useTranslation();

  const {
    control,
    watch,
    formState: { errors }
  } = useForm<FormInputs>({
    mode: 'onChange',
    resolver: yupResolver(passwordChangeSchema)
  });

  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorText, setErrorText] = useState('');
  const navigate = useNavigate();

  const buttonDisabledFlag = (): boolean =>
    watch('password') === undefined ||
    '' ||
    watch('password_confirm') === undefined ||
    '' ||
    Boolean(errors.password) ||
    Boolean(errors.password_confirm) ||
    watch('password') !== watch('password_confirm');

  const [actionCode, setActionCode] = useState<string>('');

  const queryParams = new URLSearchParams(window.location.search);
  const oobCode = queryParams.get('oobCode') ?? '';

  useEffect(() => {
    setActionCode(oobCode);
  }, []);

  const handleSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      if (oobCode === '') throw new Error(); // 取得できない場合処理終了
      await verifyPasswordResetCode(auth, actionCode);
      await confirmPasswordReset(auth, actionCode, watch('password'));
      navigate('/auth/password/complete');
    } catch (e) {
      setOpen(true);
      setErrorText(t('passwordReset.failed'));
      throwError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CustomLoading loading={loading} />
      <Grid
        container
        direction="column"
        alignItems="center"
        justifyContent="center"
      >
        <Grid item xs={12}>
          <Card sx={styles.cardSize}>
            <CardContent sx={styles.cardContentPadding}>
              <Typography
                sx={[styles.titleMb, { color: blackText }]}
                component="div"
              >
                {t('passwordReset.title')}
              </Typography>
              <Controller
                render={({ field }) => (
                  <PasswordTextFiled
                    {...field}
                    error={Boolean(errors.password)}
                    helperText={errors.password?.message}
                    labelText={t('passwordReset.new')}
                    requiredText={t('form.required')}
                    isRequired
                    id="password"
                    fullWidth
                  />
                )}
                control={control}
                name="password"
              />
              <Controller
                render={({ field }) => (
                  <PasswordTextFiled
                    {...field}
                    error={Boolean(errors.password_confirm)}
                    helperText={errors.password_confirm?.message}
                    labelText={t('passwordReset.newConfirm')}
                    requiredText={t('form.required')}
                    isRequired
                    id="password"
                    fullWidth
                  />
                )}
                control={control}
                name="password_confirm"
              />
            </CardContent>
            <CardActions sx={[styles.textMb]}>
              <Grid container direction="column" alignItems="center">
                <Button
                  disabled={buttonDisabledFlag()}
                  variant="contained"
                  fullWidth
                  sx={primaryButtonBackground}
                  onClick={() => {
                    void (async () => {
                      await handleSubmit();
                    })();
                  }}
                >
                  <Typography
                    component="div"
                    color="white"
                    sx={{ fontSize: 15 }}
                  >
                    {t('passwordReset.submit')}
                  </Typography>
                </Button>
              </Grid>
            </CardActions>
          </Card>
          <CustomSnackbar
            open={open}
            autoHideDuration={6000}
            onClose={() => setOpen(false)}
            text={errorText}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default PasswordChangeCard;
